import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import {
  Calendar, User as UserIcon, Mail as MailIcon, Gift, ShieldAlert, CreditCard,
  MessageSquare, ArrowLeft, Pencil, FileText, Ban, Plus, Trash2, RefreshCw, Send,
  ChevronRight, AlertTriangle, KeyRound, ExternalLink, ShieldCheck, Undo2, Receipt, Coins, MessagesSquare,
} from 'lucide-react';
import { EditBookingDialog } from '../../components/EditBookingDialog';import { TakePaymentDialog } from '../../components/TakePaymentDialog';
import { BookingStatusBadge } from '../../components/BookingStatusBadge';
import { BookingStatusPanel } from '../../components/BookingStatusPanel';
import { CustomerMessageDialog } from '../../components/CustomerMessageDialog';
import { BookingDiscountsWidget, BookingTotalsSummary } from '../../components/BookingDiscountsWidget';
import { RefundCreditDialog } from '../../components/RefundCreditDialog';
import { GuestFormsDialog } from '../../components/GuestFormsDialog';
import { ChatConversation } from '../../components/chat/ChatConversation';
import useMessageStream from '../../hooks/useMessageStream';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TIME_LABELS = { '10:00': '10:00 AM', '13:00': '1:00 PM', '16:00': '4:00 PM' };
const fmt$ = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const fmtDate = (iso) => {
  if (!iso) return '-';
  try { return new Date(iso).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return '-'; }
};

const TAB_DEFS = [
  { id: 'booking',      label: 'Booking',      icon: Calendar },
  { id: 'profile',      label: 'Profile',      icon: UserIcon },
  { id: 'invitations',  label: 'Invitations',  icon: MailIcon },
  { id: 'allergies',    label: 'Allergies',    icon: ShieldAlert },
  { id: 'payments',     label: 'Payments',     icon: CreditCard },
  { id: 'loyalty',      label: 'Loyalty',      icon: Gift },
  { id: 'chat',         label: 'Chat',         icon: MessagesSquare },
  { id: 'connect',      label: 'Connect',      icon: MessageSquare },
];

// Party host ↔ customer chat thread for this booking (WhatsApp-style).
const BookingChatPanel = ({ bookingId }) => {
  const [threadId, setThreadId] = useState(null);
  const [reason, setReason] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/admin/bookings/${bookingId}/thread`, { withCredentials: true });
        if (cancelled) return;
        if (res.data?.thread) setThreadId(res.data.thread.thread_id);
        else setReason(res.data?.reason || 'unavailable');
      } catch { if (!cancelled) setReason('unavailable'); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [bookingId]);

  if (loading) {
    return <div className="bg-white rounded-2xl border border-[#E8E5DF] p-8 text-center text-sm text-[#6B6B7A]" data-testid="bk-chat-loading">Loading chat…</div>;
  }
  if (!threadId) {
    return (
      <div className="bg-white rounded-2xl border border-[#E8E5DF] p-8 text-center" data-testid="bk-chat-no-host">
        <MessagesSquare className="w-8 h-8 mx-auto text-[#E8E5DF] mb-2" />
        <p className="text-sm text-[#6B6B7A]">
          {reason === 'no_host_assigned'
            ? 'Assign a host to this party (Booking tab → “Booked by”) to start a direct chat with the customer.'
            : 'No chat thread is available for this booking yet.'}
        </p>
      </div>
    );
  }
  return (
    <div className="bg-white rounded-2xl border border-[#E8E5DF] overflow-hidden h-[64vh]" data-testid="bk-chat-panel">
      <ChatConversation apiBase="staff" threadId={threadId} />
    </div>
  );
};

// ───────────────────────── PAGE ─────────────────────────
export default function CrmBookingDetail() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [packages, setPackages] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [activeTab, setActiveTab] = useState('booking');
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [msgOpen, setMsgOpen] = useState(false);
  const [waiverModalOpen, setWaiverModalOpen] = useState(false);
  const [refundOpen, setRefundOpen] = useState(false);
  const [msgChannel, setMsgChannel] = useState('email');
  const [waiverInfo, setWaiverInfo] = useState(null);
  const hasLoadedRef = useRef(false);

  const fetchAll = useCallback(async () => {
    // Only show the full-page skeleton on the FIRST load. Later refreshes
    // (payment success, SSE pushes) must NOT flip `loading`, otherwise the
    // page short-circuits to the loading div and unmounts any open dialog
    // (e.g. the Take Payment modal would lose its success step).
    if (!hasLoadedRef.current) setLoading(true);
    try {
      const [b, p, e] = await Promise.all([
        axios.get(`${API_URL}/api/admin/bookings/${bookingId}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/packages`),
        axios.get(`${API_URL}/api/admin/employees`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      const bk = b.data?.booking || b.data;
      setBooking(bk);
      setPackages(p.data || []);
      setEmployees((e.data || []).filter((emp) => emp.active !== false));
      try {
        const wRes = await axios.get(`${API_URL}/api/admin/waivers/status`, {
          params: { booking_ids: bookingId }, withCredentials: true,
        });
        setWaiverInfo((wRes.data?.statuses || {})[bookingId] || null);
      } catch (_) { /* non-blocking */ }
      if (bk?.customer_email) {
        const c = await axios.get(`${API_URL}/api/admin/customers/${encodeURIComponent(bk.customer_email)}`,
          { withCredentials: true });
        setCustomer(c.data);
      }
      hasLoadedRef.current = true;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load booking');
    } finally { setLoading(false); }
  }, [bookingId]);

  useEffect(() => { hasLoadedRef.current = false; fetchAll(); }, [fetchAll]);

  // Live refetch on SSE updates to this booking / customer
  useMessageStream((evt) => {
    if (!evt) return;
    if (evt.type === 'crm_changed' || evt.type === 'calendar_changed' || evt.type === 'booking_updated') {
      if ((evt.ids || []).includes(bookingId) || evt.section === 'bookings' || evt.section === 'customers') {
        fetchAll();
      }
    }
  });

  if (loading) {
    return <div className="p-8 text-center text-[#6B6B7A]">Loading booking…</div>;
  }
  if (!booking) {
    return (
      <div className="p-8 text-center" data-testid="booking-not-found">
        <p className="text-[#1E1E24] mb-3">Booking not found.</p>
        <Button onClick={() => navigate('/crm/bookings')} className="rounded-full bg-[#FF3B6B] text-white">Back to bookings</Button>
      </div>
    );
  }

  const isCancelled = booking.status === 'cancelled';

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto" data-testid="crm-booking-detail">
      {/* ── Header ── */}
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <button
            onClick={() => navigate('/crm/bookings')}
            className="text-xs text-[#6B6B7A] hover:text-[#FF3B6B] flex items-center gap-1 mb-2"
            data-testid="back-to-bookings"
          >
            <ArrowLeft className="w-3 h-3" /> All bookings
          </button>
          <h1 className="text-2xl sm:text-3xl text-[#1E1E24] flex items-center gap-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            {booking.child_name}'s Party
            {booking.booking_reference && (
              <span className="text-xs font-mono font-bold text-[#FF3B6B] bg-[#FFE9EE] px-2 py-1 rounded">
                {booking.booking_reference}
              </span>
            )}
            {booking.lifecycle_status && (
              <BookingStatusBadge status={booking.lifecycle_status} note={booking.status_note} testid="bk-header-status" />
            )}
            {waiverInfo && (
              <button
                onClick={() => setWaiverModalOpen(true)}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-[#06D6A0]/15 text-[#0F8B5C] hover:bg-[#06D6A0]/25 transition-colors"
                title="View all completed guest waiver / consent forms for this party"
                data-testid="bk-header-waiver">
                <ShieldCheck className="w-3 h-3" />
                {waiverInfo.count || 1} guest waiver{(waiverInfo.count || 1) === 1 ? '' : 's'}
              </button>
            )}
          </h1>
          <p className="text-sm text-[#6B6B7A] mt-1">
            <Link to={`/crm/customers`} className="hover:underline">{booking.customer_name}</Link>
            {' · '}{booking.customer_email}{booking.customer_phone ? ` · ${booking.customer_phone}` : ''}
          </p>
        </div>
      </div>

      <GuestFormsDialog
        open={waiverModalOpen}
        onOpenChange={setWaiverModalOpen}
        title={`Waiver & consent forms — ${booking.child_name || 'party'}`}
        pdfUrlFor={(f) => `${API_URL}/api/admin/waivers/${f.form_id}/pdf`}
        loader={async () => {
          const res = await axios.get(`${API_URL}/api/admin/bookings/${bookingId}/waiver-forms`, { withCredentials: true });
          return res.data;
        }}
      />

      {/* ── Tabs ── */}
      <Tabs value={activeTab} onValueChange={setActiveTab} data-testid="booking-tabs">
        <TabsList className="bg-white border border-[#E8E5DF] inline-flex flex-wrap w-full gap-1 p-1 rounded-2xl mb-4 h-auto">
          {TAB_DEFS.map(({ id, label, icon: Icon }) => (
            <TabsTrigger
              key={id} value={id}
              className="rounded-xl data-[state=active]:bg-[#FF3B6B] data-[state=active]:text-white px-3 py-2 text-xs flex items-center gap-1"
              data-testid={`bk-tab-${id}`}
            >
              <Icon className="w-3.5 h-3.5" /> {label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="booking" forceMount={true} hidden={activeTab !== 'booking'}>
          <BookingTab
            booking={booking}
            packages={packages}
            onEdit={() => setEditOpen(true)}
            onCancel={() => setCancelOpen(true)}
            onPay={() => setPayOpen(true)}
            onMessage={(ch) => { setMsgChannel(ch); setMsgOpen(true); }}
            onRefundCredit={() => setRefundOpen(true)}
            onBookingUpdated={(b) => { if (b) setBooking(b); else fetchAll(); }}
          />
        </TabsContent>
        <TabsContent value="profile" forceMount={true} hidden={activeTab !== 'profile'}>
          <ProfileTab customer={customer} refresh={fetchAll} />
        </TabsContent>
        <TabsContent value="invitations" forceMount={true} hidden={activeTab !== 'invitations'}>
          <InvitationsTab booking={booking} />
        </TabsContent>
        <TabsContent value="allergies" forceMount={true} hidden={activeTab !== 'allergies'}>
          <AllergiesTab booking={booking} />
        </TabsContent>
        <TabsContent value="payments" forceMount={true} hidden={activeTab !== 'payments'}>
          <PaymentsTab booking={booking} onTakePayment={() => setPayOpen(true)} refresh={fetchAll} />
        </TabsContent>
        <TabsContent value="loyalty" forceMount={true} hidden={activeTab !== 'loyalty'}>
          <LoyaltyTab customer={customer} refresh={fetchAll} />
        </TabsContent>
        <TabsContent value="chat" forceMount={true} hidden={activeTab !== 'chat'}>
          <BookingChatPanel bookingId={bookingId} />
        </TabsContent>
        <TabsContent value="connect" forceMount={true} hidden={activeTab !== 'connect'}>
          <ConnectTab booking={booking} customer={customer} onMessage={(ch) => { setMsgChannel(ch); setMsgOpen(true); }} />
        </TabsContent>
      </Tabs>

      {/* ── Dialogs ── */}
      <EditBookingDialog
        booking={booking}
        open={editOpen}
        onClose={() => setEditOpen(false)}
        onSaved={(updated) => { setEditOpen(false); if (updated) setBooking(updated); else fetchAll(); }}
        packages={packages}
        employees={employees}
      />
      <CancelBookingDialog
        booking={booking}
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
        onCancelled={() => { setCancelOpen(false); fetchAll(); }}
      />
      <TakePaymentDialog
        open={payOpen}
        onOpenChange={setPayOpen}
        booking={booking}
        onSuccess={() => { fetchAll(); }}
      />
      <CustomerMessageDialog
        open={msgOpen}
        onOpenChange={setMsgOpen}
        booking={booking}
        customer={{
          name: booking.customer_name,
          email: booking.customer_email,
          phone: booking.customer_phone,
        }}
        defaultChannel={msgChannel}
      />
      <RefundCreditDialog
        open={refundOpen}
        onClose={() => setRefundOpen(false)}
        booking={booking}
        onRefunded={(b) => { if (b) setBooking(b); else fetchAll(); }}
      />
    </div>
  );
}

// ───────────────────────── BOOKING TAB ─────────────────────────
function BookingTab({ booking, packages, onEdit, onCancel, onPay, onMessage, onBookingUpdated, onRefundCredit }) {
  const isCancelled = booking.status === 'cancelled';
  const addOns = (booking.add_ons || []).filter(Boolean);
  const customExtras = booking.custom_charges || [];
  const total = Number(booking.total) || 0;
  // iter-176 — paid is ledger-derived (booking.amount_paid). Fall back to the
  // legacy embedded payments array only when amount_paid isn't present.
  const paid = booking.amount_paid != null
    ? Number(booking.amount_paid) || 0
    : (booking.payments || []).reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const balanceDue = booking.balance_due ?? Math.max(0, total - paid);
  const outstanding = Math.max(0, Number(balanceDue));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="bk-tab-booking-content">
      <div className="lg:col-span-2 space-y-4">
        {/* Auto-detected booking status + note + history + manual override */}
        <BookingStatusPanel booking={booking} onChanged={() => onBookingUpdated && onBookingUpdated()} />
        {/* Party details summary */}
        <section className="bg-white rounded-2xl border border-[#E8E5DF] p-5">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h3 className="font-semibold text-[#1E1E24] flex items-center gap-2"><Calendar className="w-4 h-4" /> Party Details</h3>
            <div className="flex gap-2 flex-wrap">
              <Button onClick={onEdit} size="sm" className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white" data-testid="bk-edit-details">
                <Pencil className="w-3 h-3 mr-1" /> Edit Details
              </Button>
              <Button
                onClick={() => window.open(`${API_URL}/api/admin/bookings/${booking.booking_id}/runsheet.pdf`, '_blank', 'noopener')}
                size="sm" variant="outline" className="rounded-full" data-testid="bk-run-sheet"
              >
                <FileText className="w-3 h-3 mr-1" /> Run Sheet PDF
              </Button>
              {!isCancelled && (
                <Button onClick={onCancel} size="sm" variant="outline" className="rounded-full text-red-500 border-red-200" data-testid="bk-cancel">
                  <Ban className="w-3 h-3 mr-1" /> Cancel & Refund
                </Button>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <Stat label="Date" value={fmtDate(booking.date)} />
            <Stat label="Time" value={TIME_LABELS[booking.time_slot] || booking.time_slot} />
            <Stat label="Package" value={booking.package_name} />
            <Stat label="Guests" value={booking.guest_count} />
            <Stat label="Child" value={`${booking.child_name} (age ${booking.child_age})`} className="col-span-2" />
            <Stat label="Booked by" value={booking.booked_by_employee_name || 'Self-service'} />
            <Stat label="Status" value={booking.status_label || (booking.lifecycle_status || booking.status || 'pending')} />
          </div>
          {addOns.length > 0 && (
            <div className="mt-3 pt-3 border-t border-[#E8E5DF]">
              <p className="text-[11px] uppercase tracking-wide text-[#6B6B7A] font-semibold mb-1">Add-ons</p>
              <ul className="text-xs text-[#1E1E24] space-y-0.5">
                {addOns.map((a, i) => <li key={i}>• {a.name || a.id} · {fmt$(a.price)}{a.quantity > 1 ? ` × ${a.quantity}` : ''}</li>)}
              </ul>
            </div>
          )}
          {customExtras.length > 0 && (
            <div className="mt-3 pt-3 border-t border-[#E8E5DF]">
              <p className="text-[11px] uppercase tracking-wide text-[#6B6B7A] font-semibold mb-1">Custom Extras</p>
              <ul className="text-xs text-[#1E1E24] space-y-0.5">
                {customExtras.map((c, i) => <li key={i}>• {c.name} · {fmt$(c.unit_price)} × {c.quantity}</li>)}
              </ul>
            </div>
          )}
          {booking.edible_notes && (
            <div className="mt-3 pt-3 border-t border-[#E8E5DF]">
              <p className="text-[11px] uppercase tracking-wide text-[#6B6B7A] font-semibold mb-1">Special Instructions / Notes</p>
              <p className="text-xs text-[#1E1E24] whitespace-pre-wrap">{booking.edible_notes}</p>
            </div>
          )}
        </section>

        {/* Allergy form quick status */}
        {booking.allergy_form_completed ? (
          <div className="rounded-2xl border border-green-200 bg-green-50 p-3 text-sm text-green-700 flex items-center gap-2">
            ✓ Allergy &amp; consent form completed.
          </div>
        ) : (
          <div className="rounded-2xl border border-yellow-300 bg-yellow-50 p-3 text-sm text-yellow-800 flex items-center justify-between gap-2">
            <span className="flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Allergy form not completed yet</span>
            <a className="underline font-medium" href={`/booking/${booking.booking_id}/allergy-form`} target="_blank" rel="noreferrer">Complete →</a>
          </div>
        )}
      </div>

      {/* Right column: live totals */}
      <div className="space-y-4">
        <section className="bg-[#FFF9F5] rounded-2xl border border-[#E8E5DF] p-5 sticky top-4" data-testid="bk-totals">
          <h3 className="font-semibold text-[#1E1E24] mb-3 flex items-center gap-2">
            <CreditCard className="w-4 h-4" /> Booking Summary
          </h3>
          <BookingTotalsSummary booking={booking} />
          <div className="border-t border-[#E8E5DF] my-3" />
          {!isCancelled && outstanding > 0 && (
            <Button onClick={onPay} size="sm" className="rounded-full bg-[#06D6A0] hover:bg-[#05B88A] text-white w-full mt-3" data-testid="bk-take-payment">
              Take Payment
            </Button>
          )}
          {!isCancelled && (booking.credit_balance || 0) > 0 && (
            <Button
              onClick={onRefundCredit}
              size="sm"
              variant="outline"
              className="rounded-full border-[#06D6A0] text-[#06D6A0] hover:bg-[#06D6A0]/10 w-full mt-3"
              data-testid="bk-refund-credit"
            >
              <RefreshCw className="w-3 h-3 mr-1" /> Refund ${Number(booking.credit_balance).toFixed(2)} credit
            </Button>
          )}

          {/* Discount + Gift card quick actions */}
          {!isCancelled && (
            <div className="mt-4 pt-4 border-t border-[#E8E5DF] space-y-2" data-testid="bk-discounts-section">
              <p className="text-[10px] uppercase tracking-widest text-[#6B6B7A] font-semibold">Adjustments</p>
              <BookingDiscountsWidget booking={booking} mode="admin" compact={true} onUpdated={onBookingUpdated} />
            </div>
          )}

          <div className="grid grid-cols-2 gap-2 mt-4 pt-4 border-t border-[#E8E5DF]">
            <Button onClick={() => onMessage('email')} size="sm" variant="outline" className="rounded-full text-xs" data-testid="bk-send-email">
              <MailIcon className="w-3 h-3 mr-1" /> Email
            </Button>
            <Button onClick={() => onMessage('sms')} size="sm" variant="outline" className="rounded-full text-xs" disabled={!booking.customer_phone} data-testid="bk-send-sms">
              <MessageSquare className="w-3 h-3 mr-1" /> SMS
            </Button>
          </div>
        </section>
      </div>
    </div>
  );
}

// ───────────────────────── PROFILE TAB ─────────────────────────
function ProfileTab({ customer, refresh }) {
  const [form, setForm] = useState({ name: '', phone: '', address: '', notes: '' });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!customer) return;
    setForm({
      name: customer.name || '',
      phone: customer.phone || '',
      address: customer.address || '',
      notes: customer.notes || '',
    });
  }, [customer?.email]);   // eslint-disable-line react-hooks/exhaustive-deps

  if (!customer) {
    return <p className="text-sm text-[#6B6B7A]">Customer profile loading…</p>;
  }
  const save = async () => {
    setBusy(true);
    try {
      await axios.patch(`${API_URL}/api/admin/customers/${encodeURIComponent(customer.email)}`, {
        customer_name: form.name.trim(),
        customer_phone: form.phone.trim(),
        address: form.address.trim(),
        notes: form.notes,
        sync_to_bookings: true,
      }, { withCredentials: true });
      toast.success('Profile saved');
      refresh?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  const sendReset = async () => {
    if (!window.confirm(`Email a password-reset link to ${customer.email}?`)) return;
    try {
      await axios.post(`${API_URL}/api/admin/customers/${encodeURIComponent(customer.email)}/send-password-reset`,
        {}, { withCredentials: true });
      toast.success(`Reset link sent to ${customer.email}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  return (
    <div className="bg-white rounded-2xl border border-[#E8E5DF] p-5 space-y-4" data-testid="bk-tab-profile-content">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <h3 className="font-semibold text-[#1E1E24] flex items-center gap-2"><UserIcon className="w-4 h-4" /> Customer Profile</h3>
        <div className="flex gap-2">
          {customer.has_account && (
            <Button onClick={sendReset} size="sm" variant="outline" className="rounded-full text-xs" data-testid="bk-profile-reset">
              <KeyRound className="w-3 h-3 mr-1" /> Send Password Reset
            </Button>
          )}
          <Link
            to={`/crm/customers`}
            state={{ openEmail: customer.email }}
            className="text-xs text-[#FF3B6B] hover:underline flex items-center gap-1"
            data-testid="bk-profile-full-view"
          >
            Full customer view <ExternalLink className="w-3 h-3" />
          </Link>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-[#6B6B7A]">Name</Label>
          <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="mt-1" data-testid="bk-profile-name" />
        </div>
        <div>
          <Label className="text-xs text-[#6B6B7A]">Email</Label>
          <Input value={customer.email} readOnly className="mt-1 bg-[#FFFDF8]" />
        </div>
        <div>
          <Label className="text-xs text-[#6B6B7A]">Phone</Label>
          <Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className="mt-1" data-testid="bk-profile-phone" />
        </div>
        <div>
          <Label className="text-xs text-[#6B6B7A]">Address</Label>
          <Input value={form.address} onChange={(e) => setForm({...form, address: e.target.value})} className="mt-1" data-testid="bk-profile-address" />
        </div>
      </div>
      <div>
        <Label className="text-xs text-[#6B6B7A]">Internal Notes</Label>
        <Textarea value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} rows={3} className="mt-1" data-testid="bk-profile-notes" />
      </div>
      <div className="flex justify-end">
        <Button onClick={save} disabled={busy} className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white" data-testid="bk-profile-save">
          {busy ? 'Saving…' : 'Save Profile'}
        </Button>
      </div>
      <div className="grid grid-cols-3 gap-2 pt-3 border-t border-[#E8E5DF]">
        <Stat label="Total Bookings" value={customer.total_bookings} />
        <Stat label="Total Spent" value={fmt$(customer.total_spent)} />
        <Stat label="Outstanding" value={fmt$(customer.outstanding_balance)} />
      </div>
    </div>
  );
}

// ───────────────────────── INVITATIONS TAB ─────────────────────────
function InvitationsTab({ booking }) {
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const navigate = useNavigate();

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/invitations`, { withCredentials: true });
      const linked = (r.data || []).filter((inv) => inv.booking_id === booking.booking_id);
      setInvitations(linked);
    } catch { toast.error('Failed to load invitations'); }
    finally { setLoading(false); }
  }, [booking.booking_id]);
  useEffect(() => { reload(); }, [reload]);

  const isCancelled = booking.status === 'cancelled';

  return (
    <div className="bg-white rounded-2xl border border-[#E8E5DF] p-5 space-y-4" data-testid="bk-tab-invitations-content">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="font-semibold text-[#1E1E24] flex items-center gap-2"><MailIcon className="w-4 h-4" /> Invitations for this Party</h3>
        <Button
          size="sm"
          onClick={() => setCreateOpen(true)}
          disabled={isCancelled}
          className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white"
          data-testid="bk-create-invite"
        >
          <Plus className="w-3 h-3 mr-1" /> Create Invitation
        </Button>
      </div>
      {isCancelled && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          This booking is cancelled — new invitations can't be linked to it. Please pick another active party.
        </p>
      )}
      {loading ? <p className="text-sm text-[#6B6B7A]">Loading…</p> :
       invitations.length === 0 ? (
        <p className="text-sm text-[#6B6B7A] py-4 text-center">
          No invitations created for this party yet. Click <b>Create Invitation</b> to send out RSVP links to guests.
        </p>
      ) : invitations.map((inv) => (
        <InvitationRow key={inv.invitation_id} inv={inv} booking={booking} onChange={reload} navigate={navigate} />
      ))}
      <CreateInvitationDialog
        booking={booking}
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(inv) => { setCreateOpen(false); reload(); toast.success('Invitation created'); navigate(`/crm/invitations/${inv.invitation_id}`); }}
      />
    </div>
  );
}

function InvitationRow({ inv, booking, onChange, navigate }) {
  const remove = async () => {
    if (!window.confirm('Delete this invitation? Guests who already RSVP\'d will keep their responses on record.')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/invitations/${inv.invitation_id}`, { withCredentials: true });
      toast.success('Invitation deleted');
      onChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const duplicate = async () => {
    try {
      await axios.post(`${API_URL}/api/admin/invitations`, {
        booking_id: booking.booking_id,
        child_name: inv.child_name || booking.child_name,
        age: inv.age || booking.child_age,
        party_date: inv.party_date || booking.date,
        party_time: inv.party_time || booking.time_slot,
        location: inv.location || 'Creative Birthdays Studio',
        message: inv.message,
        template_id: inv.template_id,
      }, { withCredentials: true });
      toast.success('Invitation duplicated');
      onChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to duplicate'); }
  };
  const resend = async () => {
    try {
      await axios.post(`${API_URL}/api/admin/invitations/${inv.invitation_id}/send`, {}, { withCredentials: true });
      toast.success('Invitations re-sent');
    } catch (e) { toast.error(e.response?.data?.detail || 'Resend failed'); }
  };
  return (
    <div className="p-3 rounded-xl border border-[#E8E5DF] bg-[#FFFDF8]" data-testid={`bk-inv-${inv.invitation_id}`}>
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <button
          onClick={() => navigate(`/crm/invitations/${inv.invitation_id}`)}
          className="text-left min-w-0 flex-1 hover:opacity-90"
        >
          <p className="font-medium text-[#1E1E24] truncate">{inv.title || `${inv.child_name || booking.child_name}'s Invitation`}</p>
          <p className="text-xs text-[#6B6B7A]">{fmtDate(inv.created_at)} · {inv.recipients_count || 0} recipients · {inv.yes_count || 0} Yes / {inv.no_count || 0} No</p>
        </button>
        <div className="flex gap-1 flex-wrap">
          <Button onClick={() => navigate(`/crm/invitations/${inv.invitation_id}`)} size="sm" variant="ghost" className="h-7 text-xs" data-testid={`bk-inv-open-${inv.invitation_id}`}>
            Open <ChevronRight className="w-3 h-3 ml-0.5" />
          </Button>
          <Button onClick={resend} size="sm" variant="ghost" className="h-7 text-xs text-[#06D6A0]" data-testid={`bk-inv-resend-${inv.invitation_id}`}>
            <Send className="w-3 h-3 mr-0.5" /> Resend
          </Button>
          <Button onClick={duplicate} size="sm" variant="ghost" className="h-7 text-xs text-[#FF3B6B]" data-testid={`bk-inv-duplicate-${inv.invitation_id}`}>
            Duplicate
          </Button>
          <Button onClick={remove} size="sm" variant="ghost" className="h-7 text-xs text-red-500" data-testid={`bk-inv-delete-${inv.invitation_id}`}>
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function CreateInvitationDialog({ booking, open, onClose, onCreated }) {
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setTitle(`${booking.child_name}'s ${booking.child_age || ''} Birthday`);
    setMessage(`Hi! Please join us for ${booking.child_name}'s birthday party on ${booking.date} at ${booking.time_slot}. Hope to see you there! 🎉`);
  }, [open, booking?.child_name, booking?.child_age, booking?.date, booking?.time_slot]);
  const create = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API_URL}/api/admin/invitations`, {
        booking_id: booking.booking_id,
        title: title.trim(),
        message,
        child_name: booking.child_name,
        age: booking.child_age,
        party_date: booking.date,
        party_time: booking.time_slot,
        location: 'Creative Birthdays Studio',
        template_id: 'fruit_fiesta',
      }, { withCredentials: true });
      onCreated?.(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create'); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !busy && !o && onClose?.()}>
      <DialogContent className="bg-white max-w-md" data-testid="bk-create-invite-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#FF3B6B]" style={{ fontFamily: 'Fredoka' }}>
            Create Invitation
          </DialogTitle>
          <DialogDescription>
            Linked to <b>{booking.child_name}'s</b> party on <b>{fmtDate(booking.date)}</b>
            {booking.booking_reference && (
              <span className="ml-1 font-mono text-[10px] text-[#FF3B6B]">({booking.booking_reference})</span>
            )}.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1" data-testid="bk-invite-title" />
          </div>
          <div>
            <Label className="text-xs">Personal message</Label>
            <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4} className="mt-1" data-testid="bk-invite-message" />
          </div>
          <p className="text-[11px] text-[#6B6B7A]">After creating, you can add recipients & finalize the design on the invitation page.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} className="rounded-full">Cancel</Button>
          <Button onClick={create} disabled={busy || !title.trim()} className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white" data-testid="bk-create-invite-confirm">
            {busy ? 'Creating…' : 'Create invitation'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ───────────────────────── ALLERGIES TAB ─────────────────────────
function AllergiesTab({ booking }) {
  const [recipients, setRecipients] = useState([]);
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // recipient being filled-on-behalf
  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [invsR, formsR] = await Promise.all([
        axios.get(`${API_URL}/api/admin/invitations`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/customers/${encodeURIComponent(booking.customer_email)}`, { withCredentials: true }),
      ]);
      const linked = (invsR.data || []).filter((inv) => inv.booking_id === booking.booking_id);
      // Pull recipients for each
      const allRecipients = [];
      for (const inv of linked) {
        try {
          const r = await axios.get(`${API_URL}/api/admin/invitations/${inv.invitation_id}`, { withCredentials: true });
          for (const rcp of (r.data?.recipients || [])) {
            allRecipients.push({ ...rcp, invitation_title: inv.title });
          }
        } catch {/* skip */}
      }
      setRecipients(allRecipients);
      setForms((formsR.data?.allergy_forms || []).filter((f) => f.booking_id === booking.booking_id));
    } catch { toast.error('Failed to load allergy data'); }
    finally { setLoading(false); }
  }, [booking.booking_id, booking.customer_email]);
  useEffect(() => { reload(); }, [reload]);

  const formByRecipientId = useMemo(() => {
    const m = {};
    for (const f of forms) {
      if (f.recipient_id) m[f.recipient_id] = f;
    }
    return m;
  }, [forms]);
  const completedCount = recipients.filter((r) => formByRecipientId[r.recipient_id]).length;

  return (
    <div className="bg-white rounded-2xl border border-[#E8E5DF] p-5 space-y-4" data-testid="bk-tab-allergies-content">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="font-semibold text-[#1E1E24] flex items-center gap-2">
          <ShieldAlert className="w-4 h-4" /> Allergy &amp; Consent Forms
        </h3>
        <Badge className="bg-[#FF3B6B]/10 text-[#FF3B6B] border-none">
          {completedCount} / {recipients.length} completed
        </Badge>
      </div>
      {loading ? <p className="text-sm text-[#6B6B7A]">Loading…</p> :
       recipients.length === 0 ? (
        <p className="text-sm text-[#6B6B7A] py-4 text-center">
          No invited guests yet. Add guests via the <b>Invitations</b> tab first.
        </p>
      ) : (
        <div className="space-y-2">
          {recipients.map((rcp) => {
            const f = formByRecipientId[rcp.recipient_id];
            return (
              <div key={rcp.recipient_id} className="flex items-center justify-between gap-2 p-3 bg-[#FFFDF8] rounded-xl border border-[#E8E5DF]" data-testid={`bk-allergy-row-${rcp.recipient_id}`}>
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-[#1E1E24] truncate">{rcp.name || 'Unnamed guest'}</p>
                  <p className="text-[11px] text-[#6B6B7A]">
                    RSVP: <b>{rcp.rsvp_response || rcp.status || 'pending'}</b>
                    {' · '}{f ? <span className="text-[#06D6A0]">✓ Form complete</span> : <span className="text-amber-600">⚠ Form missing</span>}
                  </p>
                </div>
                <Button
                  size="sm" variant="outline"
                  onClick={() => setEditing({ recipient: rcp, existing: f })}
                  className="rounded-full text-xs"
                  data-testid={`bk-fill-allergy-${rcp.recipient_id}`}
                >
                  {f ? 'Edit Form' : 'Complete Form'}
                </Button>
              </div>
            );
          })}
        </div>
      )}
      <AllergyOnBehalfDialog
        recipient={editing?.recipient}
        existing={editing?.existing}
        open={!!editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); reload(); }}
      />
    </div>
  );
}

function AllergyOnBehalfDialog({ recipient, existing, open, onClose, onSaved }) {
  const [child, setChild] = useState('');
  const [allergyText, setAllergyText] = useState('');
  const [medicalText, setMedicalText] = useState('');
  const [emName, setEmName] = useState('');
  const [emPhone, setEmPhone] = useState('');
  const [photoOK, setPhotoOK] = useState(false);
  const [liability, setLiability] = useState(false);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !recipient) return;
    setChild((existing?.attendee_name) || recipient.name || '');
    // Existing form: decrypted in customer-detail response (via allergy_forms[].medical_data block)
    const med = existing?.medical_data || {};
    setAllergyText(Array.isArray(med.allergies) ? med.allergies.join(', ') : (med.allergies || med.allergy_other || ''));
    setMedicalText(med.medical_other || (Array.isArray(med.medical_conditions) ? med.medical_conditions.join(', ') : ''));
    setEmName(existing?.emergency_contact_name || '');
    setEmPhone(existing?.emergency_contact_phone || '');
    setPhotoOK(!!existing?.photo_video_consent);
    setLiability(!!existing?.liability_waiver_accepted);
    setConsent(false);   // Always force admin to re-affirm
  }, [open, recipient, existing]);

  const save = async () => {
    if (!consent) { toast.error('Parent/guardian consent must be confirmed'); return; }
    setBusy(true);
    try {
      const payload = {
        attendee_name: child,
        allergies: allergyText ? [allergyText] : [],
        allergy_other: allergyText,
        medical_conditions: medicalText ? [medicalText] : [],
        medical_other: medicalText,
        emergency_contact_name: emName,
        emergency_contact_phone: emPhone,
        photo_video_consent: photoOK,
        liability_waiver_accepted: liability,
        consent_obtained_by_admin: true,
      };
      await axios.post(
        `${API_URL}/api/admin/invitation-recipients/${recipient.recipient_id}/allergy-form`,
        payload, { withCredentials: true },
      );
      toast.success('Allergy form saved');
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally { setBusy(false); }
  };

  if (!recipient) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-lg bg-white" data-testid="bk-allergy-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#FF3B6B]" style={{ fontFamily: 'Fredoka' }}>
            <ShieldAlert className="w-5 h-5" />
            {existing ? 'Edit' : 'Complete'} allergy form
          </DialogTitle>
          <DialogDescription>
            Filling out the form on behalf of {recipient.name || 'this guest'}'s parent/guardian.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Child's name</Label>
            <Input value={child} onChange={(e) => setChild(e.target.value)} className="mt-1" data-testid="bk-allergy-child" />
          </div>
          <div>
            <Label className="text-xs">Allergies (food, environmental, medication, etc.)</Label>
            <Textarea value={allergyText} onChange={(e) => setAllergyText(e.target.value)} rows={2} className="mt-1" data-testid="bk-allergy-allergies" />
          </div>
          <div>
            <Label className="text-xs">Medical conditions / Other notes</Label>
            <Textarea value={medicalText} onChange={(e) => setMedicalText(e.target.value)} rows={2} className="mt-1" data-testid="bk-allergy-medical" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Emergency contact name</Label>
              <Input value={emName} onChange={(e) => setEmName(e.target.value)} className="mt-1" data-testid="bk-allergy-em-name" />
            </div>
            <div>
              <Label className="text-xs">Emergency phone</Label>
              <Input value={emPhone} onChange={(e) => setEmPhone(e.target.value)} className="mt-1" data-testid="bk-allergy-em-phone" />
            </div>
          </div>
          <div className="space-y-1 text-xs">
            <label className="flex items-center gap-2"><Checkbox checked={photoOK} onCheckedChange={(v) => setPhotoOK(!!v)} data-testid="bk-allergy-photo" /> Photo/video consent given</label>
            <label className="flex items-center gap-2"><Checkbox checked={liability} onCheckedChange={(v) => setLiability(!!v)} data-testid="bk-allergy-liability" /> Liability waiver accepted</label>
          </div>
          <div className="rounded-xl bg-amber-50 border border-amber-200 p-3">
            <label className="flex items-start gap-2 text-xs text-amber-900 cursor-pointer" data-testid="bk-allergy-consent-row">
              <Checkbox checked={consent} onCheckedChange={(v) => setConsent(!!v)} data-testid="bk-allergy-consent" />
              <span><b>I confirm that explicit consent has been obtained</b> from the parent/guardian to submit this form on their behalf.</span>
            </label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} className="rounded-full">Cancel</Button>
          <Button onClick={save} disabled={busy || !consent} className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white" data-testid="bk-allergy-save">
            {busy ? 'Saving…' : 'Save form'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ───────────────────────── PAYMENTS TAB ─────────────────────────
function PaymentsTab({ booking, onTakePayment, refresh }) {
  const bookingId = booking.booking_id;
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refundFor, setRefundFor] = useState(null);
  const [refundForm, setRefundForm] = useState({ amount: '', pin: '', reason: '' });
  const [refunding, setRefunding] = useState(false);
  const [creditOpen, setCreditOpen] = useState(false);
  const [creditPin, setCreditPin] = useState('');
  const [creditBusy, setCreditBusy] = useState(false);

  const fetchSummary = useCallback(async () => {
    if (!bookingId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/bookings/${bookingId}/payment-summary`, { withCredentials: true });
      setSummary(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load payments'); }
    finally { setLoading(false); }
  }, [bookingId]);
  // Refetch the timeline whenever the booking's ledger-derived paid amount
  // changes (e.g. after a page-level Record Payment / refund) so the tab,
  // timeline and header header stay in lockstep.
  useEffect(() => { fetchSummary(); }, [fetchSummary, booking.amount_paid, booking.balance_due]);

  // Stats read from the (fetchAll-refreshed) booking prop — same source as the
  // page header — so they never drift from it. Timeline history comes from the
  // payment-summary fetch.
  const total = Number(booking.total) || 0;
  const paid = Number(booking.amount_paid ?? summary?.amount_paid ?? 0);
  const credit = Number(booking.credit_balance ?? summary?.credit_balance ?? 0);
  const outstanding = Number(booking.balance_due ?? summary?.outstanding ?? Math.max(0, total - paid));
  const creditAvailable = Number(summary?.customer_credit_available ?? 0);
  const creditApplicable = Number(summary?.credit_applicable ?? 0);
  const history = summary?.history || [];

  // Running cumulative "amount paid" after each ledger row (oldest → newest).
  let _run = 0;
  const timeline = history.map((p) => {
    const amt = Number(p.amount) || 0;
    if (p.status === 'completed' && (p.type === 'payment' || p.type === 'refund')) _run = Math.round((_run + amt) * 100) / 100;
    return { ...p, running: _run };
  });

  const submitRefund = async () => {
    if (!refundFor) return;
    const amt = parseFloat(refundForm.amount);
    if (!(amt > 0)) { toast.error('Amount must be greater than $0'); return; }
    if (amt > Number(refundFor.amount) + 0.01) { toast.error('Cannot refund more than the original payment'); return; }
    if (!/^\d{4,6}$/.test(refundForm.pin)) { toast.error('Enter your 4-digit staff PIN'); return; }
    setRefunding(true);
    try {
      await axios.post(`${API_URL}/api/admin/payments/${refundFor.payment_id}/refund`,
        { amount: amt, pin: refundForm.pin, reason: refundForm.reason }, { withCredentials: true });
      toast.success(`Refunded $${amt.toFixed(2)}`);
      setRefundFor(null);
      setRefundForm({ amount: '', pin: '', reason: '' });
      await fetchSummary();      // refresh the timeline
      refresh?.();               // refresh parent booking → sidebar totals update live
    } catch (e) { toast.error(e.response?.data?.detail || 'Refund failed'); }
    finally { setRefunding(false); }
  };

  const applyCredit = async () => {
    if (!/^\d{4,6}$/.test(creditPin)) { toast.error('Enter your 4-digit staff PIN'); return; }
    setCreditBusy(true);
    try {
      const r = await axios.post(`${API_URL}/api/admin/bookings/${bookingId}/apply-credit`,
        { pin: creditPin }, { withCredentials: true });
      toast.success(`Applied ${fmt$(r.data.applied)} credit to this booking`);
      setCreditOpen(false); setCreditPin('');
      await fetchSummary();
      refresh?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to apply credit'); }
    finally { setCreditBusy(false); }
  };

  return (
    <div className="bg-white rounded-2xl border border-[#E8E5DF] p-5 space-y-4" data-testid="bk-tab-payments-content">
      <div className="grid grid-cols-3 gap-2">
        <Stat label="Total" value={fmt$(total)} />
        <Stat label="Paid" value={<span className="text-[#06D6A0]" data-testid="bk-pay-paid">{fmt$(paid)}</span>} />
        <Stat
          label={credit > 0.005 ? 'Credit owed' : 'Outstanding'}
          value={credit > 0.005
            ? <span className="text-[#06D6A0]" data-testid="bk-pay-credit">+{fmt$(credit)}</span>
            : <span className="text-[#FF3B6B]" data-testid="bk-pay-outstanding">{fmt$(outstanding)}</span>}
        />
      </div>
      <div className="flex gap-2 flex-wrap">
        {outstanding > 0.005 && (
          <Button onClick={onTakePayment} size="sm" className="rounded-full bg-[#06D6A0] hover:bg-[#05B88A] text-white" data-testid="bk-pay-record">
            <CreditCard className="w-3 h-3 mr-1" /> Record Payment
          </Button>
        )}
        {creditApplicable > 0.005 && (
          <Button onClick={() => setCreditOpen(true)} size="sm" variant="outline"
            className="rounded-full border-[#06D6A0] text-[#058B69] hover:bg-[#06D6A0]/10" data-testid="bk-pay-apply-credit">
            <Coins className="w-3 h-3 mr-1" /> Apply {fmt$(creditApplicable)} credit
          </Button>
        )}
        <Button
          onClick={() => window.open(`${API_URL}/api/public/invoice/${bookingId}/pdf`, '_blank', 'noopener')}
          size="sm" variant="outline" className="rounded-full" data-testid="bk-pay-invoice"
        >
          <FileText className="w-3 h-3 mr-1" /> Invoice PDF
        </Button>
        <Button onClick={fetchSummary} size="sm" variant="outline" className="rounded-full" data-testid="bk-pay-refresh">
          <RefreshCw className="w-3 h-3 mr-1" /> Refresh
        </Button>
      </div>

      {/* Payment timeline (ledger-derived, running balance) */}
      <div className="rounded-2xl border border-[#E8E5DF] overflow-hidden" data-testid="bk-pay-timeline">
        <div className="px-4 py-2 border-b border-[#E8E5DF] flex items-center justify-between bg-[#FFFDF8]">
          <h4 className="font-semibold text-sm text-[#1E1E24] flex items-center gap-1.5"><Receipt className="w-4 h-4" /> Payment timeline</h4>
          <span className="text-xs text-[#6B6B7A]">{timeline.length} {timeline.length === 1 ? 'entry' : 'entries'}</span>
        </div>
        {loading ? (
          <p className="text-sm text-[#6B6B7A] py-6 text-center" data-testid="bk-pay-loading">Loading…</p>
        ) : timeline.length === 0 ? (
          <p className="text-sm text-[#6B6B7A] py-6 text-center" data-testid="bk-pay-empty">No payments recorded yet.</p>
        ) : (
          <div className="divide-y divide-[#E8E5DF]">
            {timeline.map((p) => {
              const amt = Number(p.amount) || 0;
              const isRefund = p.type === 'refund' || amt < 0;
              const pending = p.status !== 'completed';
              return (
                <div key={p.payment_id} className="px-4 py-3 flex items-center gap-3 text-sm" data-testid={`bk-pay-tl-row-${p.payment_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                        isRefund ? 'bg-red-100 text-red-600'
                        : p.type === 'adjustment' ? 'bg-orange-100 text-orange-600'
                        : 'bg-[#06D6A0]/15 text-[#058B69]'}`}>{p.type}</span>
                      <span className="text-xs capitalize text-[#6B6B7A]">{(p.method || '').replace(/_/g, ' ')}</span>
                      {pending && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#FFF7EB] text-[#8A5A1F] border border-[#F5C15A]">{p.status}</span>}
                      <span className="text-[11px] text-[#6B6B7A]">{(p.created_at || '').replace('T', ' ').slice(0, 16)}</span>
                    </div>
                    {p.notes && <p className="text-xs text-[#6B6B7A] mt-0.5 truncate" title={p.notes}>{p.notes}</p>}
                    {p.employee_name && <p className="text-[11px] text-[#6B6B7A]">by {p.employee_name}</p>}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className={`font-bold ${isRefund ? 'text-[#FF3B6B]' : 'text-[#1E1E24]'}`} data-testid={`bk-pay-tl-amount-${p.payment_id}`}>
                      {isRefund ? '-' : ''}{fmt$(Math.abs(amt))}
                    </p>
                    {!pending && (
                      <p className="text-[10px] text-[#6B6B7A]" data-testid={`bk-pay-tl-running-${p.payment_id}`}>
                        Paid to date: {fmt$(p.running)}
                      </p>
                    )}
                  </div>
                  {p.type === 'payment' && p.status === 'completed' && (
                    <Button size="sm" variant="ghost"
                      onClick={() => { setRefundFor(p); setRefundForm({ amount: Number(p.amount).toFixed(2), pin: '', reason: '' }); }}
                      className="text-[#FF3B6B] hover:bg-[#FFE4EB] h-7 px-2 text-xs flex-shrink-0"
                      data-testid={`bk-pay-refund-${p.payment_id}`}>
                      <Undo2 className="w-3 h-3 mr-1" /> Refund
                    </Button>
                  )}
                  {p.type === 'refund' && p.status === 'completed' && (
                    <Button size="sm" variant="ghost"
                      onClick={() => window.open(`${API_URL}/api/admin/payments/${p.payment_id}/refund-receipt.pdf`, '_blank', 'noopener')}
                      className="text-[#6B6B7A] hover:bg-[#F3F1EC] h-7 px-2 text-xs flex-shrink-0"
                      data-testid={`bk-pay-refund-receipt-${p.payment_id}`}>
                      <FileText className="w-3 h-3 mr-1" /> Receipt
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Refund dialog */}
      <Dialog open={!!refundFor} onOpenChange={(o) => !o && setRefundFor(null)}>
        <DialogContent className="bg-white max-w-md" data-testid="bk-pay-refund-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Undo2 className="w-4 h-4 text-[#FF3B6B]" /> Refund a payment</DialogTitle>
            <DialogDescription>
              Original payment: {fmt$(refundFor?.amount || 0)} ({(refundFor?.method || '').replace(/_/g, ' ')}).
              {refundFor?.method === 'credit_card_online' && ' Stripe will be called to issue the refund.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Refund amount ($)</Label>
              <Input type="number" min="0.01" step="0.01" value={refundForm.amount}
                onChange={(e) => setRefundForm({ ...refundForm, amount: e.target.value })} className="mt-1" data-testid="bk-pay-refund-amount" />
            </div>
            <div>
              <Label className="text-xs">Reason</Label>
              <Textarea rows={2} value={refundForm.reason}
                onChange={(e) => setRefundForm({ ...refundForm, reason: e.target.value })} className="mt-1" data-testid="bk-pay-refund-reason" />
            </div>
            <div>
              <Label className="text-xs flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Your 4-digit staff PIN</Label>
              <Input type="password" inputMode="numeric" maxLength={6} value={refundForm.pin}
                onChange={(e) => setRefundForm({ ...refundForm, pin: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                className="mt-1 font-mono tracking-[0.4em] text-center" placeholder="••••" data-testid="bk-pay-refund-pin" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRefundFor(null)} className="rounded-full">Cancel</Button>
            <Button onClick={submitRefund} disabled={refunding}
              className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white" data-testid="bk-pay-refund-submit">
              {refunding ? 'Refunding…' : 'Process refund'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Apply account credit dialog */}
      <Dialog open={creditOpen} onOpenChange={(o) => !o && setCreditOpen(false)}>
        <DialogContent className="bg-white max-w-md" data-testid="bk-pay-credit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Coins className="w-4 h-4 text-[#06D6A0]" /> Apply account credit</DialogTitle>
            <DialogDescription>
              This customer has leftover credit from another (overpaid) booking. Applying it lowers
              this booking's balance — it moves money already paid, so it's not a new charge or a cash refund.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-xl bg-[#F0FBF7] border border-[#06D6A0]/30 p-3 text-sm space-y-1">
              <div className="flex justify-between"><span className="text-[#6B6B7A]">Credit available</span><span className="font-semibold" data-testid="bk-pay-credit-available">{fmt$(creditAvailable)}</span></div>
              <div className="flex justify-between"><span className="text-[#6B6B7A]">Outstanding balance</span><span className="font-semibold">{fmt$(outstanding)}</span></div>
              <div className="flex justify-between text-[#058B69] font-bold mt-1 pt-1 border-t border-[#06D6A0]/20"><span>Will apply</span><span data-testid="bk-pay-credit-amount">{fmt$(creditApplicable)}</span></div>
            </div>
            <div>
              <Label className="text-xs flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Your 4-digit staff PIN</Label>
              <Input type="password" inputMode="numeric" maxLength={6} value={creditPin}
                onChange={(e) => setCreditPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="mt-1 font-mono tracking-[0.4em] text-center" placeholder="••••" data-testid="bk-pay-credit-pin" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreditOpen(false)} className="rounded-full">Cancel</Button>
            <Button onClick={applyCredit} disabled={creditBusy}
              className="rounded-full bg-[#06D6A0] hover:bg-[#05B88A] text-white" data-testid="bk-pay-credit-submit">
              {creditBusy ? 'Applying…' : `Apply ${fmt$(creditApplicable)}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ───────────────────────── LOYALTY TAB ─────────────────────────
function LoyaltyTab({ customer, refresh }) {
  const [rewards, setRewards] = useState([]);
  const [target, setTarget] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!customer?.has_account) return;
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/admin/loyalty/customer/${encodeURIComponent(customer.email)}`,
          { withCredentials: true });
        setRewards(r.data?.rewards || []);
      } catch {/* non-fatal */}
    })();
  }, [customer?.email, customer?.has_account]);

  const redeem = async () => {
    if (!target) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API_URL}/api/admin/loyalty/redeem-for-customer`,
        { email: customer.email, reward_id: target.reward_id }, { withCredentials: true });
      const code = r.data?.voucher?.discount_code;
      toast.success(code ? `Redeemed — code: ${code}` : 'Reward redeemed');
      setTarget(null);
      refresh?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  if (!customer) return <p className="text-sm text-[#6B6B7A]">Loading…</p>;

  return (
    <div className="bg-white rounded-2xl border border-[#E8E5DF] p-5 space-y-4" data-testid="bk-tab-loyalty-content">
      <div className="rounded-2xl p-4 bg-gradient-to-r from-[#FF3B6B] to-[#FB923C] text-white">
        <p className="text-[11px] uppercase tracking-wide opacity-80">{customer.name}'s balance</p>
        <p className="text-3xl font-bold" style={{ fontFamily: 'Fredoka' }}>{customer.loyalty_balance || 0} pts</p>
      </div>
      {!customer.has_account ? (
        <p className="text-sm text-[#6B6B7A]">Customer needs an account to earn / redeem points.</p>
      ) : rewards.length === 0 ? (
        <p className="text-sm text-[#6B6B7A]">Not enough points for any reward yet.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {rewards.map((r) => (
            <button
              key={r.reward_id} onClick={() => setTarget(r)}
              className="text-left px-3 py-2 rounded-lg border border-[#E8E5DF] bg-[#FFFDF8] hover:border-[#FF3B6B] transition"
              data-testid={`bk-loyalty-${r.reward_id}`}
            >
              <p className="text-sm font-semibold text-[#1E1E24]">{r.name}</p>
              <div className="flex items-center justify-between mt-0.5">
                <p className="text-[10px] text-[#6B6B7A]">{r.type === 'discount' ? `$${r.discount_value} off` : 'Free add-on'}</p>
                <p className="text-[10px] font-bold text-[#FF3B6B]">{r.points_cost} pts</p>
              </div>
            </button>
          ))}
        </div>
      )}
      {(customer.loyalty_history || []).length > 0 && (
        <div className="pt-3 border-t border-[#E8E5DF] space-y-2">
          <p className="text-xs uppercase tracking-wide font-semibold text-[#6B6B7A]">Recent activity</p>
          {(customer.loyalty_history || []).slice(0, 8).map((h, i) => (
            <div key={h.ledger_id || i} className="flex justify-between text-xs">
              <span className="truncate flex-1 text-[#1E1E24]">{h.description}</span>
              <span className={`flex-shrink-0 font-semibold ${h.points >= 0 ? 'text-[#06D6A0]' : 'text-[#FF3B6B]'}`}>
                {h.points >= 0 ? '+' : ''}{h.points} pts
              </span>
            </div>
          ))}
        </div>
      )}
      <Dialog open={!!target} onOpenChange={(o) => !o && setTarget(null)}>
        <DialogContent className="bg-white max-w-sm" data-testid="bk-loyalty-confirm">
          <DialogHeader>
            <DialogTitle className="text-[#FF3B6B]" style={{ fontFamily: 'Fredoka' }}>Redeem reward?</DialogTitle>
            <DialogDescription>
              Deduct <b>{target?.points_cost} pts</b> from {customer.name}'s balance for <b>{target?.name}</b>.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTarget(null)} disabled={busy} className="rounded-full">Cancel</Button>
            <Button onClick={redeem} disabled={busy} className="rounded-full bg-[#FF3B6B] text-white" data-testid="bk-loyalty-confirm-btn">
              {busy ? 'Redeeming…' : 'Redeem'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ───────────────────────── CONNECT TAB ─────────────────────────
function ConnectTab({ booking, customer, onMessage }) {
  return (
    <div className="bg-white rounded-2xl border border-[#E8E5DF] p-5 space-y-4" data-testid="bk-tab-connect-content">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="font-semibold text-[#1E1E24] flex items-center gap-2"><MessageSquare className="w-4 h-4" /> Communicate with {booking.customer_name}</h3>
        <Link
          to={`/crm/reminders?email=${encodeURIComponent(booking.customer_email)}`}
          target="_blank"
          className="text-xs text-[#FF3B6B] hover:underline flex items-center gap-1"
          data-testid="bk-open-reminders"
        >
          Open Reminders Page <ExternalLink className="w-3 h-3" />
        </Link>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Button onClick={() => onMessage('email')} className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white" data-testid="bk-connect-email">
          <MailIcon className="w-4 h-4 mr-1" /> Send Email (pre-filled)
        </Button>
        <Button onClick={() => onMessage('sms')} disabled={!booking.customer_phone}
          className="rounded-full bg-[#06D6A0] hover:bg-[#05B88A] text-white" data-testid="bk-connect-sms">
          <MessageSquare className="w-4 h-4 mr-1" /> Send SMS (pre-filled)
        </Button>
      </div>
      <p className="text-xs text-[#6B6B7A]">
        Templates available: party reminder (7d / 2d), thank-you, custom. SMS uses Twilio. All messages are logged in the Communications history below.
      </p>
      {(customer?.communications || []).length === 0 ? (
        <p className="text-sm text-[#6B6B7A] py-4 text-center">No communications sent yet.</p>
      ) : (
        <div className="space-y-2 pt-3 border-t border-[#E8E5DF]">
          <p className="text-xs uppercase tracking-wide font-semibold text-[#6B6B7A]">History ({(customer?.communications || []).length})</p>
          {(customer?.communications || []).slice(0, 15).map((c, i) => (
            <div key={i} className="flex items-start gap-2 p-2 rounded-lg bg-[#FFFDF8] border border-[#E8E5DF] text-xs" data-testid={`bk-connect-row-${i}`}>
              <MailIcon className="w-3.5 h-3.5 text-[#6B6B7A] mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-[#1E1E24] truncate">{c.subject || c.kind || 'Email'}</p>
                <p className="text-[10px] text-[#6B6B7A]">{fmtDate(c.sent_at)} · {c.status || 'sent'}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ───────────────────────── CANCEL DIALOG ─────────────────────────
function CancelBookingDialog({ booking, open, onClose, onCancelled }) {
  const [reason, setReason] = useState('');
  const [pin, setPin] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) { setReason(''); setPin(''); setPreview(null); return; }
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/admin/bookings/${booking.booking_id}/cancel-preview`, { withCredentials: true });
        setPreview(r.data);
      } catch {/* non-fatal */}
    })();
  }, [open, booking?.booking_id]);
  const cancel = async () => {
    if (!pin) { toast.error('PIN required'); return; }
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/admin/bookings/${booking.booking_id}/cancel`, { reason, pin }, { withCredentials: true });
      toast.success('Booking cancelled & slot released');
      onCancelled?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !busy && !o && onClose?.()}>
      <DialogContent className="bg-white max-w-md" data-testid="bk-cancel-dialog">
        <DialogHeader>
          <DialogTitle className="text-red-600 flex items-center gap-2" style={{ fontFamily: 'Fredoka' }}>
            <Ban className="w-5 h-5" /> Cancel booking?
          </DialogTitle>
          <DialogDescription>
            This will release the slot. Any deposit will require a refund.
          </DialogDescription>
        </DialogHeader>
        {preview && (
          <div className="rounded-xl bg-[#FFFDF8] border border-[#E8E5DF] p-3 text-xs space-y-1">
            <Row label="Total paid" value={fmt$(preview.paid_amount)} />
            <Row label="Suggested refund" value={fmt$(preview.refund_amount)} muted />
          </div>
        )}
        <div className="space-y-2">
          <div>
            <Label className="text-xs">Reason</Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} className="mt-1" data-testid="bk-cancel-reason" />
          </div>
          <div>
            <Label className="text-xs">Admin / Manager PIN</Label>
            <Input type="password" maxLength={4} value={pin} onChange={(e) => setPin(e.target.value)} className="mt-1" data-testid="bk-cancel-pin" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} className="rounded-full">Cancel</Button>
          <Button onClick={cancel} disabled={busy || !pin} className="rounded-full bg-red-500 hover:bg-red-600 text-white" data-testid="bk-cancel-confirm">
            {busy ? 'Cancelling…' : 'Confirm Cancel'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ───────────────────────── Utils ─────────────────────────
const Stat = ({ label, value, className = '' }) => (
  <div className={className}>
    <p className="text-[10px] uppercase tracking-wide text-[#6B6B7A]">{label}</p>
    <p className="text-sm font-medium text-[#1E1E24]">{value}</p>
  </div>
);
const Row = ({ label, value, muted = false, color }) => (
  <div className="flex items-center justify-between">
    <span className={muted ? 'text-[#6B6B7A]' : 'text-[#1E1E24]'} style={color ? { color } : undefined}>{label}</span>
    <span className={muted ? 'text-[#6B6B7A]' : 'text-[#1E1E24]'} style={color ? { color } : undefined}>{value}</span>
  </div>
);
